import _ from "lodash";
import axios from "axios";
import { Alert, Box, Grid, Typography } from "@mui/material";
import TextBlock from "Components/Layout/TextBlock";
import { useState } from "react";
import { useHistory, useParams } from "react-router";
import { useAsync } from "react-use";
import InlineList from "../../../Components/InlineList";
import Layout from "../../../Components/Layout/Layout";
import { Plate } from "../../../Components/Plate";
import { Sale, SaleEmail, SaleOrder, SaleOrderStatus, ThemeColor, UserRole } from "../../../system/Domain";
import useEvent from "../../../system/useEvent";
import SaleEmailCard from "./SaleEmailCard";
import SalesAddressInfo from "./SalesAddressInfo";
import SalesDocumentCard from "./SalesDocumentCard";
import SalesInvoiceCard from "./SalesInvoiceCard";
import SalesOrderCard from "./SalesOrderCard";
import SalesOrderSidebar from "./SalesOrderSidebar";
import ShippingCard from "./ShippingCard";
import SaleExportCard from "./SaleExportCard";
import useUser from "../../../system/useUser";

export const saleOrderStatusLabels = {
	"Active": "Bestellt",
	"Invoiced": "Verrechnet",
	"Paid": "Bezahlt",
	"Cancelled": "Abgebrochen"
};

const saleOrderStatusColors = {
	"Active": "info",
	"Invoiced": "primary",
	"Paid": "success",
	"Cancelled": "warning"
};

export const paintSaleOrderStatus = (status: SaleOrderStatus) => (saleOrderStatusColors[status] as ThemeColor);

export interface CurrentSaleOrder {
	sale: Sale
	order: SaleOrder
}

const SalesOrderPage = () => {
	const params = useParams<{ id: string, orderId: string }>();
	const orderId = Number.parseInt(params.orderId);
	const [saleOrder, setSaleOrder] = useState<CurrentSaleOrder | null>(null);
	const history = useHistory();
	const [, , hasRole] = useUser();

	const order = saleOrder?.order;
	const sale = saleOrder?.sale;

	useAsync(async () => {
		if (!params) {
			return null;
		}

		const { data: sale } = await axios.get<Sale>(`/api/sales/${params.id}`);
		const order = sale.orders.find(o => o.id === orderId);

		if (!order) {
			return null;
		}

		setSaleOrder({
			sale,
			order
		});
	}, [params]);

	const handleSalesChanged = (sale: Sale) => {
		const order = sale.orders.find(o => o.id === orderId);

		if (!order) {
			setSaleOrder(null);
			return;
		}

		setSaleOrder({
			sale: sale,
			order
		});
	};

	useEvent("sale:updated", async data => {
		const { id } = params;

		const { data: updatedSale } = await axios.get<Sale>(`/api/sales/${id}`);

		handleSalesChanged(updatedSale);
	}, [params]);

	const handleSelectEmail = (email: SaleEmail) => {
		history.push(`/sales/${sale!.id}/orders/${order!.id}/emails/${email.mailTrackingId}`)
	};

	const lines = order?.lines || [];
	const invoiceIds = _.groupBy(lines, "invoiceId");

	const canReclassifyExport =
		hasRole(UserRole.VehicleExportManager)
		&& (saleOrder?.order?.export?.canReclassify ?? false);

	return (
		<Layout
			title="Fahrzeugverkauf"
			subtitle={order ? `Status ${saleOrderStatusLabels[order.status]}` : undefined}
			sidebar={(
				<SalesOrderSidebar
					currentSaleOrder={saleOrder}
					onChange={handleSalesChanged}
					canReclassifyExport={canReclassifyExport}
				/>
			)}
		>
			{order && sale && (
				<Box>
					<Grid container spacing={2} justifyContent="space-between">
						<Grid item>
							<TextBlock
								primary={<Plate plateNumber={sale.plateNumber} />}
								secondary={
									<InlineList>
										{sale.vin}
										{sale.vehicle.businessLine}
									</InlineList>
								}
							/>
						</Grid>
					</Grid>
					{!order.buyer.email?.trim()
						&& (
							sale.salesChannel.id === "DE - BCA"
							|| sale.salesChannel.id === "DE - CARMARKET AUCTION"
							|| sale.salesChannel.id === "DE - CARMARKET INSTANT AUCTION"
						) && (
						<Box mt={2}>
							<Alert severity="warning">
								Die E-Mail-Adresse des Käufers ist nicht hinterlegt. Das Hochladen der unterzeichneten Pickup Order wird blockiert, bis die E-Mail-Adresse gesetzt wurde.
							</Alert>
						</Box>
					)}
					{order.status === "Cancelled" && (
						<Box marginY={4}>
							<Typography>Verkauf an</Typography>
							<SalesAddressInfo address={order.buyer} />
						</Box>
					)}
					<Box py={4}>
						<Grid container spacing={2}>
							<>
								{order.status === "Cancelled" && (
									<Grid item xs={12}>
										<Typography>
											Die Bestellung wurde abgebrochen
										</Typography>
									</Grid>
								)}
								{(order.status === "Invoiced" || order.status === "Paid")
									&& Object.keys(invoiceIds)
										.map(invoiceId => invoiceId)
										.map(invoiceId => (
											<Grid key={invoiceId} item md={12} lg={6} sx={{
												flexGrow: 1
											}}>
												<SalesInvoiceCard
													invoiceId={invoiceId !== "null" ? invoiceId : null}
													currentSalesOrder={saleOrder}
													onChange={handleSalesChanged}
												/>
											</Grid>
										))
								}
								{order.status === "Active" && (
									<Grid item md={12} lg={6} sx={{
										flexGrow: 1
									}}>
										<SalesOrderCard
											sale={sale}
											order={order}
											onSaleChanged={handleSalesChanged}
										/>
									</Grid>
								)}
								<Grid item md={12} lg={6} sx={{
									flexGrow: 1
								}}>
									<SalesDocumentCard
										sale={saleOrder?.sale}
										order={saleOrder?.order}
										onSaleChanged={handleSalesChanged}
									/>
								</Grid>
								{order.emails && (
									<Grid item md={12} lg={6} sx={{
										flexGrow: 1
									}}>
										<SaleEmailCard
											emails={order.emails}
											onSelect={handleSelectEmail}
										/>
									</Grid>
								)}
								{sale && sale.shipping && (
									<Grid item md={12} lg={6} sx={{
										flexGrow: 1
									}}>
										<ShippingCard
											sale={sale}
											shipping={sale.shipping}
										/>
									</Grid>
								)}
								{sale && order?.export && (
									<Grid item md={12} lg={6} sx={{
										flexGrow: 1
									}}>
										<SaleExportCard
											sale={sale}
											orderId={order?.id}
										/>
									</Grid>
								)}
							</>
						</Grid>
					</Box>
				</Box>
			)}
		</Layout>
	);
};

export default SalesOrderPage;
